import React, { memo, useCallback, useMemo, useState } from 'react'

import {
    buildDailyOccupancy,
    buildMonthGroups,
    buildProjectTimelineRows,
    formatCapacityHours,
    getStatusColor,
    getTimelineSegmentStyle,
} from '../capacity.utils'
import CapacityTooltip from './CapacityTooltip'
import styles from '../ProjectCapacityDashboard.module.css'

const DAY_COL_WIDTH = 28

const CapacityTimeline = memo(({ users, rangeStart, rangeEnd }) => {
    const [tooltip, setTooltip] = useState({
        visible: false,
        segment: null,
        x: 0,
        y: 0,
    })

    const days = useMemo(() => {
        if (!users?.length) return []
        return buildDailyOccupancy(users[0], rangeStart, rangeEnd)
    }, [users, rangeStart, rangeEnd])

    const monthGroups = useMemo(() => buildMonthGroups(days), [days])

    const showTooltip = useCallback((segment, event) => {
        setTooltip({
            visible: true,
            segment,
            x: event.clientX + 12,
            y: event.clientY + 12,
        })
    }, [])

    const hideTooltip = useCallback(() => {
        setTooltip((prev) => ({ ...prev, visible: false }))
    }, [])

    const gridTemplate = useMemo(
        () => `200px repeat(${Math.max(days.length, 1)}, ${DAY_COL_WIDTH}px)`,
        [days.length]
    )

    if (!users?.length || !days.length) return null

    return (
        <section className={styles.timelineSection}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Capacity Timeline</h3>
                <div className={styles.legend}>
                    <span className={styles.legendItem}>
                        <span
                            className={styles.legendDot}
                            style={{ background: getStatusColor('available') }}
                        />
                        Available
                    </span>
                    <span className={styles.legendItem}>
                        <span
                            className={styles.legendDot}
                            style={{ background: getStatusColor('partial') }}
                        />
                        Partial
                    </span>
                    <span className={styles.legendItem}>
                        <span
                            className={styles.legendDot}
                            style={{ background: getStatusColor('occupied') }}
                        />
                        Occupied
                    </span>
                </div>
            </div>

            <div className={styles.timelineScroll}>
                <div
                    className={styles.timelineGrid}
                    style={{ gridTemplateColumns: gridTemplate }}
                >
                    <div
                        className={`${styles.timelineStickyCell} ${styles.timelineHeaderCell}`}
                    >
                        User
                    </div>
                    {monthGroups.map((group) => (
                        <div
                            key={group.key}
                            className={styles.timelineMonthCell}
                            style={{ gridColumn: `span ${group.span}` }}
                        >
                            {group.label}
                        </div>
                    ))}

                    <div
                        className={`${styles.timelineStickyCell} ${styles.timelineSubHeaderCell}`}
                    />
                    {days.map((day) => (
                        <div
                            key={day.date}
                            className={styles.timelineDayLabel}
                            title={day.dayjs.format('DD MMM YYYY')}
                        >
                            {day.label}
                        </div>
                    ))}

                    {users.map((user) => {
                        const daily = buildDailyOccupancy(
                            user,
                            rangeStart,
                            rangeEnd
                        )
                        const projectRows = buildProjectTimelineRows(
                            user,
                            rangeStart,
                            rangeEnd
                        )

                        return (
                            <React.Fragment key={user.userId}>
                                <div
                                    className={`${styles.timelineStickyCell} ${styles.timelineUserCell}`}
                                >
                                    {user.userName}
                                </div>
                                {daily.map((day) => (
                                    <div
                                        key={`${user.userId}-${day.date}`}
                                        className={styles.timelineDayCell}
                                        style={{
                                            backgroundColor: `${getStatusColor(day.status)}22`,
                                            borderBottom: `3px solid ${getStatusColor(day.status)}`,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (day.segments[0]) {
                                                showTooltip(day.segments[0], e)
                                            }
                                        }}
                                        onMouseLeave={hideTooltip}
                                    />
                                ))}

                                {projectRows.map((project) => (
                                    <React.Fragment
                                        key={`${user.userId}-${project.projectId}`}
                                    >
                                        <div
                                            className={`${styles.timelineStickyCell} ${styles.timelineProjectCell}`}
                                        >
                                            <span>{project.projectName}</span>
                                            <span className={styles.projectHours}>
                                                {formatCapacityHours(project.hours)}
                                            </span>
                                        </div>
                                        <div
                                            className={styles.timelineBarTrack}
                                            style={{
                                                gridColumn: `2 / span ${days.length}`,
                                            }}
                                        >
                                            {project.tasks.map((task) => {
                                                const style =
                                                    getTimelineSegmentStyle(
                                                        task,
                                                        rangeStart,
                                                        rangeEnd,
                                                        days.length
                                                    )
                                                if (!style) return null

                                                return (
                                                    <div
                                                        key={`${project.projectId}-${task.taskName}`}
                                                        className={
                                                            styles.timelineBarSegment
                                                        }
                                                        style={{
                                                            left: `${style.leftPercent}%`,
                                                            width: `${style.widthPercent}%`,
                                                        }}
                                                        onMouseEnter={(e) =>
                                                            showTooltip(
                                                                {
                                                                    projectName:
                                                                        project.projectName,
                                                                    taskName:
                                                                        task.taskName,
                                                                    totalHours:
                                                                        task.hours,
                                                                    startDate:
                                                                        task.startDate,
                                                                    endDate:
                                                                        task.endDate,
                                                                },
                                                                e
                                                            )
                                                        }
                                                        onMouseLeave={hideTooltip}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>

            <CapacityTooltip {...tooltip} />
        </section>
    )
})

CapacityTimeline.displayName = 'CapacityTimeline'

export default CapacityTimeline
